import fs from 'node:fs'
import path from 'node:path'

function getSourceLines(namespace: string, hasSrcDir: boolean): string[] {
  return hasSrcDir
    ? [`@source "../../${namespace}/**/*.{ts,tsx}";`, `@source "./(${namespace})/**/*.{ts,tsx}";`]
    : [`@source "../${namespace}/**/*.{ts,tsx}";`, `@source "./(${namespace})/**/*.{ts,tsx}";`]
}

// Tailwind v4 @theme tokens that map CSS vars to utility classes.
// These let Tailwind generate bg-primary, text-muted-foreground, border-border, etc.
const ADMIN_THEME_BLOCK = `
@theme inline {
  --color-background: var(--background);
  --color-foreground: var(--foreground);
  --color-card: var(--card);
  --color-card-foreground: var(--card-foreground);
  --color-popover: var(--popover);
  --color-popover-foreground: var(--popover-foreground);
  --color-primary: var(--primary);
  --color-primary-foreground: var(--primary-foreground);
  --color-secondary: var(--secondary);
  --color-secondary-foreground: var(--secondary-foreground);
  --color-muted: var(--muted);
  --color-muted-foreground: var(--muted-foreground);
  --color-accent: var(--accent);
  --color-accent-foreground: var(--accent-foreground);
  --color-destructive: var(--destructive);
  --color-destructive-foreground: var(--destructive-foreground);
  --color-border: var(--border);
  --color-input: var(--input);
  --color-input-border: var(--input-border);
  --color-ring: var(--ring);
  --color-chart-1: var(--chart-1);
  --color-chart-2: var(--chart-2);
  --color-chart-3: var(--chart-3);
  --color-chart-4: var(--chart-4);
  --color-chart-5: var(--chart-5);
  --color-sidebar: var(--sidebar);
  --color-sidebar-foreground: var(--sidebar-foreground);
  --color-sidebar-primary: var(--sidebar-primary);
  --color-sidebar-primary-foreground: var(--sidebar-primary-foreground);
  --color-sidebar-accent: var(--sidebar-accent);
  --color-sidebar-accent-foreground: var(--sidebar-accent-foreground);
  --color-sidebar-border: var(--sidebar-border);
  --color-sidebar-ring: var(--sidebar-ring);
  --radius-sm: calc(var(--radius) - 4px);
  --radius-md: calc(var(--radius) - 2px);
  --radius-lg: var(--radius);
  --radius-xl: calc(var(--radius) + 4px);
}`

const ADMIN_SHADOW_INPUT_UTILITY = `
@utility shadow-input {
  --admin-shadow-input: rgba(0, 0, 0, 0.02) 0px 4px 8px -2px;
  --tw-shadow: var(--admin-shadow-input);
  box-shadow: var(--tw-inset-shadow), var(--tw-inset-ring-shadow),
    var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow);
}`

function getAdminThemeVars(): string[] {
  return ADMIN_THEME_BLOCK.split('\n').filter((line) => line.trim().startsWith('--'))
}

function getMissingThemeVars(existingBlock: string): string[] {
  return getAdminThemeVars()
    .filter((line) => {
      const varName = line.trim().split(':')[0]!.trim()
      return !existingBlock.includes(varName)
    })
    .map((line) => `  ${line.trim()}`)
}

/**
 * Find the main CSS file that imports Tailwind.
 */
function findMainCss(cwd: string): string | undefined {
  const candidates = [
    'src/app/globals.css',
    'app/globals.css',
    'src/app/global.css',
    'app/global.css',
    'src/app/app.css',
    'app/app.css',
    'src/globals.css',
    'globals.css'
  ]

  for (const candidate of candidates) {
    const filePath = path.join(cwd, candidate)
    if (fs.existsSync(filePath)) {
      return filePath
    }
  }
  return undefined
}

/**
 * Append @source directive and @theme tokens to the user's main CSS file
 * so Tailwind scans Admin component classes and generates utility classes.
 */
export function scaffoldTailwind(
  cwd: string,
  hasSrcDir: boolean,
  namespace = 'admin'
): { file: string | null; appended: boolean } {
  const cssFile = findMainCss(cwd)
  if (!cssFile) {
    return { file: null, appended: false }
  }

  let content = fs.readFileSync(cssFile, 'utf-8')
  let changed = false

  // 1. Add @source directives if not present
  const sourceLines = getSourceLines(namespace, hasSrcDir)
  const missingLines = sourceLines.filter((sl) => !content.includes(sl))
  if (missingLines.length > 0) {
    const lines = content.split('\n')
    let insertIndex = 0
    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i]!.trim()
      if (
        trimmed.startsWith('@import') ||
        trimmed.startsWith('@source') ||
        trimmed.startsWith('@plugin')
      ) {
        insertIndex = i + 1
      }
    }
    lines.splice(insertIndex, 0, ...missingLines)
    content = lines.join('\n')
    changed = true
  }

  // 2. Add @theme tokens, merging future tokens into existing blocks.
  const themeMatch = content.match(/@theme\s+inline\s*\{/)
  if (themeMatch && themeMatch.index !== undefined) {
    let braceDepth = 0
    let insertPos = -1
    for (let i = themeMatch.index; i < content.length; i++) {
      if (content[i] === '{') braceDepth++
      if (content[i] === '}') {
        braceDepth--
        if (braceDepth === 0) {
          insertPos = i
          break
        }
      }
    }
    if (insertPos !== -1) {
      const existingBlock = content.slice(themeMatch.index, insertPos)
      const varsOnly = getMissingThemeVars(existingBlock).join('\n')
      if (varsOnly) {
        content = `${content.slice(0, insertPos)}${varsOnly}\n${content.slice(insertPos)}`
        changed = true
      }
    }
  } else {
    content = `${content}\n${ADMIN_THEME_BLOCK}\n`
    changed = true
  }

  if (!content.includes('@utility shadow-input')) {
    content = `${content}\n${ADMIN_SHADOW_INPUT_UTILITY}\n`
    changed = true
  }

  if (changed) {
    fs.writeFileSync(cssFile, content, 'utf-8')
  }

  return { file: cssFile, appended: changed }
}
